import { ArrowLeft, ClipboardCheck, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { formatMetricValue } from '@/lib/metric-format';
import { formatDateTimeValue, formatDateValue, toNumber, toIsoDate } from '@/lib/utils';

import { ControlArea } from '../components/meta-campos';
import { MetaProgress } from '../components/meta-progress';
import { MetasNav } from '../components/metas-nav';
import { useMeta, useMetaEvaluaciones } from '../hooks/use-metas';
import { useEvaluarMeta } from '../hooks/use-metas-mutations';
import { avance, formatoDeUnidad, valorConUnidad } from '../lib/metas-format';

/**
 * Registro de una evaluación de meta (`/metas/:id/evaluar`).
 *
 * El backend exige notas y la marca de "en camino"; la fecha es opcional y por
 * defecto se usa la del día.
 */
export function MetaEvaluarPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();

  const meta = useMeta(Number.isFinite(id) ? id : null);
  const evaluaciones = useMetaEvaluaciones(Number.isFinite(id) ? id : null);
  const evaluar = useEvaluarMeta();

  const [notas, setNotas] = useState('');
  const [enCamino, setEnCamino] = useState(true);
  const [fecha, setFecha] = useState(() => toIsoDate(new Date()));
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const goal = meta.data;

  useDocumentTitle(goal ? `Metas · Evaluar ${goal.goal_name}` : 'Metas · Evaluar');

  if (meta.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 rounded-card" />
        <Skeleton className="h-72 rounded-card" />
      </div>
    );
  }

  if (meta.isError || !goal) {
    return (
      <Alert variant="error" title="No se pudo cargar la meta">
        <p>La meta no existe o no tienes acceso a ella.</p>
        <Link to="/metas/listado" className="mt-2 inline-flex text-sm font-semibold text-brand-700">
          Volver al listado
        </Link>
      </Alert>
    );
  }

  const formato = formatoDeUnidad(goal.unit_measurement);
  const previas = evaluaciones.data ?? [];

  const enviar = () => {
    const encontrados: Record<string, string> = {};

    if (!notas.trim()) encontrados.notas = 'Las notas de la evaluación son obligatorias.';
    if (!fecha) encontrados.fecha = 'Selecciona la fecha de la evaluación.';

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) {
      setAviso('Completa los campos obligatorios.');
      return;
    }

    evaluar.mutate(
      {
        id: goal.id,
        payload: { evaluation_notes: notas.trim(), is_on_track: enCamino, evaluation_date: fecha },
      },
      {
        onSuccess: () => navigate(`/metas/${goal.id}`, { replace: true }),
        onError: (error) => setAviso(error.message || 'No se pudo registrar la evaluación.'),
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="space-y-4">
        <Link
          to={`/metas/${goal.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 transition-colors hover:text-brand-700"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Volver al detalle
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Metas</p>
            <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
              <ClipboardCheck className="size-6 shrink-0 text-brand-500" aria-hidden="true" />
              Evaluar meta
            </h1>
            <p className="mt-1 text-sm text-ink-500">{goal.goal_name}</p>
          </div>

          <MetasNav active="listado" className="justify-end" />
        </div>
      </header>

      {aviso ? (
        <Alert variant="error" title="No se pudo guardar">
          {aviso}
        </Alert>
      ) : null}

      <SectionCard title="Situación actual" description="Datos que se guardarán junto con la evaluación">
        <MetaProgress goal={goal} detallado />

        <div className="mt-4 grid gap-3 border-t border-ink-100 pt-4 sm:grid-cols-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Acumulado
            </p>
            <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink-900">
              {formatMetricValue(goal.current_value, formato)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Objetivo
            </p>
            <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink-900">
              {valorConUnidad(goal.target_value, goal.unit_measurement)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Avance
            </p>
            <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink-900">
              {avance(goal).toFixed(2)} %
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Nueva evaluación" description="Deja constancia del avance y las acciones">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Fecha de la evaluación"
            type="date"
            value={fecha}
            error={errores.fecha}
            onChange={(event) => setFecha(event.target.value)}
          />

          <div className="flex flex-col justify-end gap-1.5 pb-1">
            <Checkbox
              label="El avance va en camino"
              hint="Desmárcalo si la meta está en riesgo de no cumplirse."
              checked={enCamino}
              onChange={(event) => setEnCamino(event.target.checked)}
            />
          </div>

          <ControlArea
            containerClassName="sm:col-span-2"
            label="Notas de la evaluación"
            rows={4}
            value={notas}
            error={errores.notas}
            placeholder="Ej. El avance se mantiene por encima del esperado; se refuerza la promoción de diésel."
            onChange={(event) => setNotas(event.target.value)}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ink-100 pt-4">
          {!enCamino ? (
            <p className="flex items-center gap-2 text-xs text-amber-700">
              <TriangleAlert className="size-3.5" aria-hidden="true" />
              Se registrará como evaluación en riesgo.
            </p>
          ) : null}

          <div className="ml-auto flex items-center gap-3">
            <Link
              to={`/metas/${goal.id}`}
              className="inline-flex h-11 items-center rounded-control border border-ink-200 bg-white px-5 text-sm font-semibold text-ink-800 transition-colors hover:border-brand-300 hover:text-brand-700"
            >
              Cancelar
            </Link>
            <Button loading={evaluar.isPending} onClick={enviar}>
              Guardar evaluación
            </Button>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Evaluaciones anteriores"
        description="Historial de seguimiento registrado"
        action={<Badge variant="outline">{previas.length}</Badge>}
      >
        {evaluaciones.isPending ? (
          <Skeleton className="h-40 rounded-card" />
        ) : previas.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">
            Todavía no hay evaluaciones registradas para esta meta.
          </p>
        ) : (
          <ul className="space-y-3">
            {previas.map((evaluacion) => (
              <li key={evaluacion.id} className="rounded-control border border-ink-100 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-ink-900">
                      {formatDateValue(evaluacion.evaluation_date)}
                    </span>
                    <Badge variant={evaluacion.is_on_track ? 'success' : 'danger'}>
                      {evaluacion.is_on_track ? 'En camino' : 'En riesgo'}
                    </Badge>
                  </div>
                  <span className="font-mono text-xs text-ink-500 tabular-nums">
                    {toNumber(evaluacion.progress_percentage).toFixed(2)} %
                  </span>
                </div>

                {evaluacion.evaluation_notes ? (
                  <p className="mt-2 text-sm leading-relaxed text-ink-700">
                    {evaluacion.evaluation_notes}
                  </p>
                ) : null}

                <p className="mt-1.5 text-[11px] text-ink-400">
                  {[evaluacion.first_name, evaluacion.last_name].filter(Boolean).join(' ') ||
                    'Sistema'}{' '}
                  · {formatDateTimeValue(evaluacion.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
